// src/admin/SetTextPreview.jsx
//
// Read-only proof-read preview of a set_text item, rendered the way a student sees it.
// Ported (structure + section labels) from the prototype SetTextAnalysisView
// (between-the-lines-v2.html L14804-14945), SIMPLIFIED for an admin proof aid:
//   - Mind map = a simple nested list (root -> branches -> leaves), NOT the SVG
//     VisualMindMap (that lands with the real student frontend later).
//   - Audio = a placeholder line per ref ("audio: <title>"), NOT a player / signed URL.
//   - Annotated/Highlight (glossary + search) -> plain text.
//
// PURE RENDER: it takes content already in memory (the editor's live draft) and renders
// it. No Firestore read, no write, no gating/entitlement, no routing. This component is
// intentionally the reusable core of the future student reader.
//
// FIELD MAPPING: content is read in SCHEMA names. To keep ONE mapping source (no
// divergent translation), we run it through normalizeSetTextContent (setTextContent.js)
// which also tolerates legacy aliases (visualMindMap/essays/audios) and partial input.
import React, { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { normalizeSetTextContent } from './setTextContent.js';
import { ReadingPrefsContext } from '../pages/useReadingPrefs.js';
import { portalSkinClass } from '../pages/readingPrefs.js';
// In-reader glossary highlighting. AnnotatedText is a PASS-THROUGH when there is no
// AnnotationsContext provider (e.g. the admin proof preview), so wrapping prose here is
// safe everywhere and only highlights inside a reader that supplies a matcher
// (PackReaderSetText / PackReaderGrouped). Prose only -- never headings/labels/citations.
import AnnotatedText from '../components/AnnotatedText.jsx';
import RichText from '../components/richText/RichText.jsx';
import { stripMarkdown } from '../components/richText/richTextPipeline.js';
import LessonCard, { LessonCardHeading } from '../components/reader/LessonCard.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

const has = (v) => typeof v === 'string' && v.trim().length > 0;
const nonEmptyList = (a) => (Array.isArray(a) ? a.filter((x) => has(x)) : []);

// Model-essay level styling -- prototype L14918-14921 (title + border colour per level).
const ESSAY_LEVEL_META = {
    l3: { title: 'Clear Understanding', color: 'border-blue-900/50' },
    l4: { title: 'Confident Analysis', color: 'border-book-accent/30' },
    l5: { title: 'Perceptive Insight', color: 'border-book-accent' }
};

// lesson-3a: the prototype's per-section icons (star / brain / layers / feather) and
// the gold-rule SectionHeading are gone -- every block now opens with the same small
// LessonCardHeading the rest of the lesson uses. One heading style, not five.

// Mind map -- faithful CSS/flexbox tree ported from the prototype VisualMindMap
// (between-the-lines-v2.html L9699-9746). Pure JSX + Tailwind, NO SVG, NO library;
// takes the exact { root, branches:[{title, leaves:[strings]}] } shape (empty leaves filtered).
function VisualMindMap({ data }) {
    const root = (data && data.root) || '';
    const branches = (data && Array.isArray(data.branches)) ? data.branches : [];
    return (
        <div className="flex flex-col md:flex-row items-start md:items-center justify-start w-full py-1">
            {/* Root */}
            <div className="flex items-center shrink-0 mb-4 md:mb-0 w-full md:w-[25%] lg:w-[22%]">
                <div className="bg-book-spine border-2 border-book-accent p-3 lg:p-4 rounded shadow-[0_0_15px_rgba(201,160,106,0.2)] z-20 text-center w-full self-center">
                    <h4 className="font-display text-book-accent font-bold text-xs lg:text-sm leading-tight">{root}</h4>
                </div>
                <div className="hidden md:block w-4 lg:w-8 border-t-2 border-book-accent/60 relative z-10 shrink-0" />
            </div>
            {/* Branches */}
            <div className="flex flex-col justify-center w-full md:w-[75%] lg:w-[78%]">
                {branches.map((branch, i) => {
                    const leaves = nonEmptyList(branch && branch.leaves);
                    return (
                        <div key={i} className="flex flex-col md:flex-row items-start md:items-stretch relative w-full py-1 md:py-1.5">
                            <div className="hidden md:block absolute left-0 w-4 lg:w-6 border-t-2 border-book-accent/60 top-1/2 -translate-y-1/2 z-10" />
                            <div className={`hidden md:block absolute left-0 border-l-2 border-book-accent/60 z-0 ${i === 0 && branches.length > 1 ? 'top-1/2 bottom-0' : ''} ${i === branches.length - 1 && branches.length > 1 ? 'top-0 bottom-1/2' : ''} ${i > 0 && i < branches.length - 1 ? 'top-0 bottom-0' : ''}`} />
                            <div className="flex items-center ml-4 md:ml-4 lg:ml-6 z-20 w-[90%] md:w-[40%] lg:w-[38%] shrink-0 mb-2 md:mb-0">
                                <div className="bg-book-page border border-book-accent/70 px-3 lg:px-4 py-1 lg:py-1.5 rounded shadow-md w-full text-left">
                                    <span className="font-serif font-bold text-book-text text-sm lg:text-base leading-tight">{(branch && branch.title) || `Branch ${i + 1}`}</span>
                                </div>
                            </div>
                            <div className="flex flex-col justify-center w-full md:w-[60%] lg:w-[62%] relative">
                                {leaves.map((leaf, j) => (
                                    <div key={j} className="flex items-center relative py-0.5 md:py-1 w-full">
                                        <div className="hidden md:block absolute left-0 w-4 lg:w-6 border-t border-book-accent/40 top-1/2 -translate-y-1/2 z-10" />
                                        <div className={`hidden md:block absolute left-0 border-l border-book-accent/40 z-0 ${j === 0 && leaves.length > 1 ? 'top-1/2 bottom-0' : ''} ${j === leaves.length - 1 && leaves.length > 1 ? 'top-0 bottom-1/2' : ''} ${j > 0 && j < leaves.length - 1 ? 'top-0 bottom-0' : ''}`} />
                                        <div className="ml-10 md:ml-4 lg:ml-6 z-20 w-[85%] md:w-full md:pr-2 lg:pr-4">
                                            <div className="bg-book-spine/50 border border-book-muted/30 px-3 py-1 rounded w-full font-serif text-book-text/90 shadow-sm text-xs lg:text-sm hover:border-book-accent/60 transition-colors">{leaf}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// lesson-3a: `eyebrow` and `done` let the reader open a set text with the same lesson
// header card every other section kind uses; the admin proof preview keeps its own
// "Set Text - Preview" label by falling back to the default.
export default function SetTextPreview({ content, eyebrow = 'Set Text · Preview', done = false }) {
    const c = normalizeSetTextContent(content);

    const outline = nonEmptyList(c.outline);
    const themes = (c.themes || []).filter((t) => t && (has(t.theme) || has(t.quote) || has(t.analysis) || has(t.connection) || has(t.error)));
    const mm = c.mindMap || { root: '', branches: [] };
    const mmBranches = (mm.branches || []).filter((b) => b && (has(b.title) || nonEmptyList(b.leaves).length));
    const mmHas = has(mm.root) || mmBranches.length > 0;
    const essayLevels = Object.keys(c.modelEssays || {}).filter((k) => has(c.modelEssays[k]));
    const audio = (c.audioRefs || []).filter((a) => a && (has(a.title) || has(a.src)));

    const anyContent = has(c.coreIdea) || mmHas || outline.length || themes.length || essayLevels.length || audio.length || has(c.fullText);

    return (
        <div className="max-w-3xl mx-auto pb-8">
            {/* lesson-3a: the shared lesson header card, so a set text opens exactly
                like a technique or a Lit* section. The author/year line follows it. */}
            <LessonHeader eyebrow={eyebrow} title={c.title || 'Untitled set text'} blocks={c} done={done} />
            {has(c.author) && (
                <p className="font-serif italic text-book-muted text-sm -mt-4 mb-6">by {c.author}{c.year ? ` · ${c.year}` : ''}</p>
            )}

            {!anyContent && (
                <p className="font-serif italic text-book-muted text-sm">Nothing to preview yet — enter some content in the editor.</p>
            )}

            {/* Audio (placeholder) */}
            {audio.length > 0 && (
                <div className="mb-6">
                    <LessonCardHeading>Audio</LessonCardHeading>
                    <div className="space-y-2">
                        {audio.map((a, i) => (
                            <LessonCard key={i} className="font-serif text-book-muted text-sm">
                                🔊 audio: {a.title || a.src || `Audio ${i + 1}`}
                                <span className="font-serif italic text-book-muted/60 text-[10px] ml-2">(player shown to students later)</span>
                            </LessonCard>
                        ))}
                    </div>
                </div>
            )}

            {/* Core Idea -> the same key-idea callout every other lesson kind uses. */}
            {has(c.coreIdea) && (
                <div data-explain-passages={JSON.stringify([c.coreIdea])} className="mb-6 rounded-[var(--radius-card)] border border-book-accent/40 bg-book-accent/5 p-4">
                    <div className="font-display text-book-accent text-xs mb-1.5 font-semibold">Core idea</div>
                    <p className="font-accent italic text-book-text text-base md:text-lg leading-relaxed whitespace-pre-wrap"><AnnotatedText>{c.coreIdea}</AnnotatedText></p>
                </div>
            )}

            {/* Mind Map -- the CSS tree keeps its OWN inner layout; only the box around
                it becomes a card. */}
            {mmHas && (
                <div className="mb-6">
                    <LessonCardHeading>Mind map</LessonCardHeading>
                    <LessonCard className="overflow-x-auto">
                        <VisualMindMap data={{ root: mm.root, branches: mmBranches }} />
                    </LessonCard>
                </div>
            )}

            {/* Structural Outline (prototype L14849-14873) */}
            {outline.length > 0 && (
                <div className="mb-6">
                    <LessonCardHeading>Structural outline</LessonCardHeading>
                    <LessonCard data-explain-passages={JSON.stringify(outline)}>
                        <ul className="space-y-3">
                            {outline.map((item, idx) => {
                                const [boldPart, ...rest] = item.includes(':') ? item.split(':') : [item];
                                const tail = rest.join(':');
                                return (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-book-accent mt-1 leading-none">✦</span>
                                        <p className="text-base leading-relaxed text-book-text opacity-90 whitespace-pre-wrap">
                                            <span className="font-bold text-book-accentDark">{boldPart}</span>{tail && <>: {tail}</>}
                                        </p>
                                    </li>
                                );
                            })}
                        </ul>
                    </LessonCard>
                </div>
            )}

            {/* Theme Analysis (prototype L14875-14908) */}
            {themes.length > 0 && (
                <div className="mb-6">
                    <LessonCardHeading>Theme analysis</LessonCardHeading>
                    <div className="flex flex-col space-y-3">
                        {themes.map((t, idx) => (
                            <LessonCard key={idx} data-explain-passages={JSON.stringify([t.theme, t.quote, t.error, t.analysis, t.connection].filter(has))} className="border-l-2 border-l-book-accent flex flex-col md:flex-row gap-5">
                                <div className="md:w-1/3 flex flex-col justify-between gap-4">
                                    <div>
                                        {has(t.theme) && <h4 className="font-display text-book-accent text-xs mb-2 font-semibold">{t.theme}</h4>}
                                        {has(t.quote) && <p className="font-serif italic text-base text-white font-semibold leading-snug whitespace-pre-wrap">&ldquo;{t.quote}&rdquo;</p>}
                                    </div>
                                    {has(t.error) && (
                                        <div className="pt-4 border-t border-book-accent/20">
                                            <span className="text-xs text-book-muted block mb-1 font-semibold">Common Error</span>
                                            <p className="text-base text-red-300/80 font-serif italic whitespace-pre-wrap"><AnnotatedText>{t.error}</AnnotatedText></p>
                                        </div>
                                    )}
                                </div>
                                <div className="md:w-2/3 flex flex-col justify-between gap-4">
                                    {has(t.analysis) && (
                                        <div>
                                            <span className="text-xs text-book-muted block mb-1 font-semibold">Analysis</span>
                                            <p className="text-base leading-relaxed text-book-text opacity-90 whitespace-pre-wrap"><AnnotatedText>{t.analysis}</AnnotatedText></p>
                                        </div>
                                    )}
                                    {has(t.connection) && (
                                        <div className="bg-black/30 p-3 border border-book-accent/10">
                                            <span className="text-xs text-book-accent block mb-1 font-semibold">Theme Connection</span>
                                            <p className="text-base font-serif italic text-book-text opacity-80 whitespace-pre-wrap"><AnnotatedText>{t.connection}</AnnotatedText></p>
                                        </div>
                                    )}
                                </div>
                            </LessonCard>
                        ))}
                    </div>
                </div>
            )}

            {/* Model Essays -- watermark cards (prototype L14910-14935); click a card to
                read the full essay in a comfortable modal (teaser + Tap to expand). */}
            {essayLevels.length > 0 && (
                <ModelEssays levels={essayLevels} modelEssays={c.modelEssays} />
            )}

            {/* Full extract (only if entered) */}
            {has(c.fullText) && (
                <div className="mb-6">
                    <LessonCardHeading>Full extract</LessonCardHeading>
                    <LessonCard className="font-serif text-book-text text-sm whitespace-pre-wrap leading-relaxed">{c.fullText}</LessonCard>
                </div>
            )}
        </div>
    );
}

// ---- Model Essays: teaser cards + click-to-read modal ----
// Each card is a teaser; clicking it opens the full essay in a comfortable reading modal.
// Levels are pre-filtered to non-empty essays by the caller (M5), so every card has text.

const ESSAY_FADE_MASK = 'linear-gradient(to bottom, #000 72%, transparent)';

const IconExpand = ({ className }) => (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>);
const IconClose = ({ className }) => (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>);

function ModelEssays({ levels, modelEssays }) {
    const [openLevel, setOpenLevel] = useState(null);
    return (
        <div className="mb-8">
            <LessonCardHeading>Model essays</LessonCardHeading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {levels.map((lvl) => (
                    <EssayCard key={lvl} lvl={lvl} text={modelEssays[lvl]} onOpen={() => setOpenLevel(lvl)} />
                ))}
            </div>
            {openLevel && (
                <EssayModal lvl={openLevel} text={modelEssays[openLevel]} onClose={() => setOpenLevel(null)} />
            )}
        </div>
    );
}

function EssayCard({ lvl, text, onOpen }) {
    // Keep the clipped button teaser plain; the full answer uses Essay Guide's renderer.
    const summary = stripMarkdown(text);
    const meta = ESSAY_LEVEL_META[lvl.toLowerCase()] || { title: '', color: 'border-book-accent/30' };
    const pRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);
    // Only fade + show "Tap to expand" when the teaser is actually clipped (M1) -- short
    // essays render in full with no fade.
    useEffect(() => {
        const el = pRef.current;
        if (el) setOverflowing(el.scrollHeight > el.clientHeight + 4);
    }, [text]);

    return (
        <button type="button" onClick={onOpen} title="Read the full essay"
            className={`text-left rounded-[var(--radius-card)] border ${meta.color} bg-book-page shadow-[var(--shadow-card)] p-4 flex flex-col relative overflow-hidden group hover:bg-book-accent/5 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-book-accent`}>
            <div className="absolute top-0 right-0 p-2 opacity-10 font-display text-5xl font-bold text-book-accent">{lvl.toUpperCase()}</div>
            <h4 className="font-display text-book-accent text-base mb-1 flex justify-between items-center gap-2">
                <span>{lvl.toUpperCase()} {meta.title && <span className="text-xs opacity-60 font-serif font-semibold text-book-muted">{meta.title}</span>}</span>
                <IconExpand className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </h4>
            <div className="h-px w-full bg-book-line mb-3" />
            <p ref={pRef}
                className="text-sm leading-relaxed text-book-muted group-hover:text-book-text transition-colors text-justify whitespace-pre-wrap max-h-56 overflow-hidden"
                style={overflowing ? { WebkitMaskImage: ESSAY_FADE_MASK, maskImage: ESSAY_FADE_MASK } : undefined}>
                <AnnotatedText>{summary}</AnnotatedText>
            </p>
            {overflowing && (
                <div className="mt-2 font-display text-book-accent text-xs opacity-80 group-hover:opacity-100 transition-opacity font-semibold">Tap to expand</div>
            )}
        </button>
    );
}

function EssayModal({ lvl, text, onClose }) {
    const meta = ESSAY_LEVEL_META[lvl.toLowerCase()] || { title: '' };
    // Portals preserve React context but NOT CSS ancestor inheritance / DOM-ancestor-based
    // handlers, so we RE-APPLY both here (mirrors ReaderAiDrawer):
    //  - THEME: the reader's live day/night skin via colorMode (ReadingPrefsContext). In the
    //    admin preview there is no provider -> createContext's DEFAULT_PREFS, so no crash.
    //  - COPY-GUARD: the `analysis-content` class on the modal panel. user-select:none is a
    //    global CSS rule (index.css) and useContentProtection's listeners are document-level
    //    and match `.analysis-content`, so the portaled modal is protected like in-page text.
    const { colorMode } = useContext(ReadingPrefsContext);
    const skin = portalSkinClass(colorMode);
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Portal to document.body so the fixed overlay is positioned relative to the VIEWPORT
    // (centered, in view immediately on open) regardless of page scroll or an ancestor
    // transform -- e.g. the reader shell's page-fade-in.
    return createPortal(
        <div className={skin}>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}
                role="dialog" aria-modal="true" aria-label={`${lvl.toUpperCase()} model essay`}>
                <div className="analysis-content w-full max-w-2xl max-h-[88vh] overflow-y-auto border border-book-accent/30 bg-book-pageDark"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="sticky top-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-book-accent/20 bg-book-pageDark">
                        <h3 className="font-display text-book-accent text-lg flex items-baseline gap-2">
                            {lvl.toUpperCase()}
                            {meta.title && <span className="text-xs opacity-70 font-serif font-semibold text-book-muted">{meta.title}</span>}
                        </h3>
                        <button onClick={onClose} aria-label="Close" className="text-book-muted hover:text-book-accent transition-colors flex-shrink-0">
                            <IconClose className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="px-6 py-5">
                        <RichText text={text} className="font-serif text-book-text text-[15px] leading-loose text-justify break-words" />
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
